/**
 * Normalizes hospital search API responses into hospitals[] + optional pagination.
 */

import type { Hospital, HospitalSearchPaginatedData, PaginationInfo } from './types';

export const isHospitalSearchPaginatedData = (v: unknown): v is HospitalSearchPaginatedData => {
  if (!v || typeof v !== 'object') return false;
  return 'data' in v && 'pagination' in v;
};

export const normalizeHospitalsFromSearchResponse = (
  searchResponse: unknown
): { hospitals: Hospital[]; pagination: PaginationInfo | null } => {
  const empty = { hospitals: [], pagination: null as PaginationInfo | null };

  if (!searchResponse || typeof searchResponse !== 'object') return empty;
  const r = searchResponse as {
    success?: boolean;
    data?: unknown;
  };
  if (!r?.success || r?.data == null) return empty;

  const filterValid = (h: unknown): h is Hospital =>
    !!h &&
    typeof h === 'object' &&
    typeof (h as Hospital).id === 'number' &&
    (h as Hospital).id > 0 &&
    typeof (h as Hospital).name === 'string' &&
    (h as Hospital).name.trim() !== '';

  if (isHospitalSearchPaginatedData(r.data)) {
    return {
      hospitals: (r.data.data as unknown[]).filter(filterValid),
      pagination: r.data.pagination as PaginationInfo,
    };
  }

  if (Array.isArray(r.data)) {
    return { hospitals: (r.data as unknown[]).filter(filterValid), pagination: null };
  }

  return empty;
};
